import { useCallback, useState } from 'react'

import { DocumentStaticActionType } from '@views/documents/elements/DocumentStaticActions'
import { FileListItem } from '@views/documents/elements/FileList'

import type { DmsDocument } from '../types'

interface UseDocumentPreviewActionsOptions {
  documents: DmsDocument[]
  deleteItem: (id: string) => void
  closePreviewModal: () => void
  openPreviewModal: () => void
  onCopyLink?: (document: DmsDocument) => void
  onMove?: (document: DmsDocument) => void
}

export const useDocumentPreviewActions = ({
  documents,
  deleteItem,
  closePreviewModal,
  openPreviewModal,
  onCopyLink,
  onMove,
}: UseDocumentPreviewActionsOptions) => {
  const [selectedDocument, setSelectedDocument] = useState<DmsDocument | null>(null)

  const selectAndOpenDocument = useCallback(
    (documentId: string) => {
      const targetDocument = documents.find((document) => document.id === documentId)
      if (targetDocument) {
        setSelectedDocument(targetDocument)
        openPreviewModal()
      }
    },
    [documents, openPreviewModal],
  )

  const handleRowClick = useCallback(
    (item: FileListItem) => {
      selectAndOpenDocument(item.id)
    },
    [selectAndOpenDocument],
  )

  const handlePreviewModalAction = useCallback(
    (action: DocumentStaticActionType) => {
      if (!selectedDocument) {
        return
      }
      if (action === DocumentStaticActionType.CopyLink) {
        onCopyLink?.(selectedDocument)
      }
      if (action === DocumentStaticActionType.Delete) {
        closePreviewModal()
        deleteItem(selectedDocument.id)
      }
      if (action === DocumentStaticActionType.Move) {
        onMove?.(selectedDocument)
      }
    },
    [closePreviewModal, deleteItem, onCopyLink, onMove, selectedDocument],
  )

  return { selectedDocument, handleRowClick, handlePreviewModalAction, selectAndOpenDocument }
}
